/**
 * <s-header-scroll> — scroll state for the fixed storefront header.
 *
 * Place it inside the <header> it controls. As soon as the page is scrolled past
 * `threshold` pixels, the header and the root element get `data-scrolled`
 * (solid ivory bar, slightly shorter; CSS reads `--mm-header-h` from the root).
 * The header never hides, whatever the scroll direction.
 *
 * On the homepage the header starts with `data-transparent`: it stays transparent
 * over the hero only while the page is at the top and nobody hovers or focuses it.
 * All visual changes are CSS (`data-transparent:` / `data-scrolled:` utilities).
 *
 * Attributes:
 *   threshold — pixels scrolled before the header turns solid (default 8).
 */
class SHeaderScroll extends HTMLElement {
  #header = null;
  #frame = 0;
  #engaged = false;
  #transparentCapable = false;
  #abort = null;

  connectedCallback() {
    this.#header = this.closest('header');
    if (!this.#header) return;
    this.#transparentCapable = this.#header.hasAttribute('data-transparent');
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const update = () => this.#schedule();
    window.addEventListener('scroll', update, { passive: true, signal });
    window.addEventListener('pageshow', update, { signal });
    if (this.#transparentCapable) {
      this.#header.addEventListener('pointerenter', () => { this.#engaged = true; this.#schedule(); }, { signal });
      this.#header.addEventListener('pointerleave', () => { this.#engaged = false; this.#schedule(); }, { signal });
      this.#header.addEventListener('focusin', () => { this.#engaged = true; this.#schedule(); }, { signal });
      this.#header.addEventListener('focusout', (event) => {
        if (this.#header.contains(event.relatedTarget)) return;
        this.#engaged = false;
        this.#schedule();
      }, { signal });
    }
    this.#apply();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#apply());
  }

  #apply() {
    const threshold = Number(this.getAttribute('threshold')) || 8;
    const scrolled = window.scrollY > threshold;
    this.#header.toggleAttribute('data-scrolled', scrolled);
    document.documentElement.toggleAttribute('data-header-scrolled', scrolled);
    if (this.#transparentCapable) {
      this.#header.toggleAttribute('data-transparent', !scrolled && !this.#engaged);
    }
  }
}

if (!customElements.get('s-header-scroll')) {
  customElements.define('s-header-scroll', SHeaderScroll);
}
